import React, { useEffect, useState } from 'react';
import images from '../../constants/images';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header/Header';
import '../../assets/styles/global.css';
import Map from '../../components/map/Map';
import styles from './About.module.css';
import Footer from '../../components/footer/Footer';
import { motion } from 'framer-motion';
import CursorFollower from '../../components/cursor/Cursor';
import '../spinner.css';

const About: React.FC = () => {
  const sections = [
    {
      id: 'vision',
      title: 'ourVision.ourVisionTitle',
      detail: 'ourVision.ourVisionDetail',
      image: images.ourVision,
    },
    {
      id: 'mission',
      title: 'ourMission.ourMissionTitle',
      detail: 'ourMission.ourMissionDetail',
      image: images.ourMission,
    },
  ];
  const ourTeam = [
    {
      image: images.person1,
      personName: 'ourTeam.person1Name',
      personRole: 'ourTeam.person1Role',
    },
    {
      image: images.person2,
      personName: 'ourTeam.person2Name',
      personRole: 'ourTeam.person2Role',
    },
    {
      image: images.person3,
      personName: 'ourTeam.person3Name',
      personRole: 'ourTeam.person3Role',
    },
    {
      image: images.person4,
      personName: 'ourTeam.person4Name',
      personRole: 'ourTeam.person4Role',
    },

    {
      image: images.person6,
      personName: 'ourTeam.person6Name',
      personRole: 'ourTeam.person6Role',
    },
    {
      image: images.person7,
      personName: 'ourTeam.person7Name',
      personRole: 'ourTeam.person7Role',
    },
    {
      image: images.person8,
      personName: 'ourTeam.person8Name',
      personRole: 'ourTeam.person8Role',
    },
    {
      image: images.person9,
      personName: 'ourTeam.person9Name',
      personRole: 'ourTeam.person9Role',
    },
    {
      image: images.person10,
      personName: 'ourTeam.person10Name',
      personRole: 'ourTeam.person10Role',
    },
    {
      image: images.person11,
      personName: 'ourTeam.person11Name',
      personRole: 'ourTeam.person11Role',
    },
  ];
  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');

    const timer = setTimeout(
      () => {
        setIsLoading(false);
        sessionStorage.setItem('hasLoadedBefore', 'true'); // mark as loaded
      },
      hasLoadedBefore ? 1000 : 3000,
    ); // shorter delay if already loaded

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 410); // Show button after scrolling 200px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToContact = (event: any) => {
    event.preventDefault(); // Prevent default anchor behavior
    const headerHeight = document.querySelector('header')?.offsetHeight || 0; // Get header height
    const targetPosition = document.getElementById('bottom')?.offsetTop || 0;

    window.scrollTo({
      top: targetPosition - headerHeight, // Subtract the header height
      behavior: 'smooth', // Smooth scrolling effect
    });
  };

  const handleNext = () => {
    setCurrentSection((prev) => (prev + 1) % sections.length);
  };

  const handlePrevious = () => {
    setCurrentSection((prev) => (prev - 1 + sections.length) % sections.length);
  };

  const title = t('heroSection.heroTitle');
  const words = title.split(' ');

  if (isLoading) {
    return (
      <>
        <div className="loader-wrapper">
          <div className="spinner">
            <div className="cube1"></div>
            <div className="cube2"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CursorFollower />

      {/* Hero Section */}
      <section
        id="top"
        className="relative top-0 left-0 z-50 font flex flex-col justify-center items-center min-h-screen"
        style={{ scrollMarginTop: '0px' }}
      >
        {/* Header */}
        <Header />

        {/* Background Image */}
        <img
          src={images.heroSection}
          alt="Aluminium Background"
          className="absolute inset-0 object-cover w-screen h-full"
        />

        {/* Overlay */}
        <div className="absolute inset-0"></div>

        {/* Content on top of the image */}
        <div
          className={`relative z-10 flex flex-col justify-between items-center sm:text-center text-left text-white px-4 xl:px-72 2xl:px-80`}
        >
          {/* Responsive Heading */}
          <h1 className="title md:text-center text-4xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-bold pb-4">
            {words.map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 50 }} // Start with invisible and slightly below
                animate={{ opacity: 1, y: 0 }} // Animate to fully visible and centered
                transition={{
                  delay: index * 0.2, // Delay each word animation
                  type: 'spring',
                  stiffness: 100,
                  damping: 25,
                }}
                className="inline-block mr-2" // Add some space between words
              >
                {word}
              </motion.span>
            ))}
          </h1>
        </div>

        {/* Scroll Button */}
        <div className="hidden lg:flex absolute lg:bottom-0 xl:bottom-0 flex-col items-center">
          <a
            onClick={handleScrollToContact}
            href="#bottom"
            className="flex items-center justify-center color text-white  lg:w-[60px] lg:h-[60px] xl:w-[75px] xl:h-[75px] 2xl:w-[105px] 2xl:h-[105px]  hover:bg-red-500 transition duration-300"
          >
            {/* Icon and Text Container */}
            <div className="flex flex-col items-center">
              {/* Arrow Icon */}
              <img
                src={images.down}
                alt="Down Arrow"
                className="w-4 md:w-5 lg:w-5 xl:w-6 2xl:w-7 h-auto mb-1"
              />
              {/* Scroll Text */}
              <span className="text-xs md:text-sm lg:text-sm xl:text-base 2xl:text-lg">
                {t('heroSection.scroll')}
              </span>
            </div>
          </a>
        </div>
        {/* WhatsApp Floating Button */}
        <a
          href="https://wa.me/919909998890"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed bottom-10 right-5 sm:right-4 md:right-4 lg:right-8 xl:right-10 bg-green-500 text-white rounded-full p-2 lg:p-3 shadow-lg hover:bg-green-600 transition z-50 ${styles.whatsappBackground}`}
        >
          {/* WhatsApp Image Icon */}
          <img
            src={images.whatsapp_white}
            alt="WhatsApp Icon"
            className={`w-6 lg:w-7 h-auto ${styles.whatsappIcon}`}
          />
        </a>
      </section>

      {/* Who we Are Section */}
      <section
        className="relative font py-20 md:py-32 2xl:py-28 px-2 md:px-8 lg:px-8 xl:px-16 2xl:px-16 bg-no-repeat bg-cover bg-bottom"
        id="bottom"
      >
        {/* Background Image */}
        <img
          src={images.about_mobile_bg}
          alt="Background"
          className={`absolute object-cover xl:hidden lg:hidden md:hidden sm:block bottom-2/4 z-0 ${styles.background}`}
          style={{ objectPosition: 'bottom' }}
        />
        <img
          src={images.about_desktop_bg}
          alt="Background"
          className={`absolute object-cover hidden xl:block lg:block md:block xl:w-full 2xl:left-0  xl:left-0 xl:bottom-0 lg:left-4 lg:-bottom-4 md:-left-0 bottom-2/4 left-6 z-0 ${styles.background}`}
          style={{ objectPosition: 'bottom' }}
        />
        {/* Overlay for darker effect */}
        <div className="absolute inset-0 z-10"></div>

        {/* Main Content */}
        <div className="relative z-20 flex flex-col lg:flex-row items-center lg:space-x-10">
          {/* Left Section - Text */}
          <div
            className={`lg:w-1/2 pb-8 lg:pb-0 px-4 lg:px-0 ${styles.section}`}
          >
            <h2
              className={`title text-4xl md:text-4xl 2xl:text-5xl font-bold pb-6 text-black ${styles.we}`}
            >
              {t('whoWeAre.title')}
            </h2>
            <p
              className={`text-lg md:text-xl lg:text-base xl:text-base 2xl:text-lg text-black font-normal pb-6 ${styles.intro}`}
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              {t('whoWeAre.intro')}
            </p>
            <p
              className={`text-lg md:text-xl lg:text-base xl:text-base 2xl:text-lg text-black font-normal pb-6 ${styles.intro}`}
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              {t('whoWeAre.detail')}
            </p>
            <p
              className={`text-lg md:text-xl lg:text-base xl:text-base 2xl:text-lg text-black font-normal ${styles.intro}`}
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              {t('whoWeAre.para')}
            </p>
          </div>

          {/* Right Section - Image */}
          <div className={`lg:w-1/2`}>
            <img
              src={images.whoWeAre}
              alt="Innovative Aluminium Solutions"
              className={`w-full max-h-min ${styles.who}`}
            />
          </div>
        </div>

        {/* Scroll to Top Button */}
        {showButton && (
          <div className="fixed xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform z-50 ">
            <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16 "></div>
            <div
              className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex "
              onClick={scrollToTop}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={images.up}
                alt="UpArrow"
                className={`flex justify-center xl:pb-8 xl:h-16 lg:h-12 lg:pb-6 ${styles.icon}`}
              />
              <button
                className={`flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-lg text-red-500 rotate-90 transition duration-300 ${styles.top}`}
              >
                {t('whoWeAre.top')}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ourVision and ourMission Section */}
      <section className={`relative font w-full md:h-96 p-4 ${styles.image}`}>
        {/* Background Image using img tag */}
        <img
          src={sections[currentSection].image}
          alt="Section Background"
          className={`absolute inset-0 w-full h-full object-cover ${styles.ourVision}`}
        />

        {/* Dark overlay to enhance text readability */}
        <div className="absolute inset-0"></div>

        {/* Main content container */}
        <div
          className={`relative z-10 mx-auto py-4 px-4 md:py-16 md:px-8 2xl:px-10 items-center ${styles.content}`}
        >
          {/* Text Content */}
          <div
            className={`text-white space-y-4 max-w-5xl 2xl:max-w-6xl ${styles.container}`}
          >
            <h2
              className={`text-3xl title md:text-3xl xl:text-4xl 2xl:text-5xl font-semibold pb-4 ${styles.title}`}
            >
              {t(sections[currentSection].title)}
            </h2>
            <p
              className={`text-base md:text-lg 2xl:text-xl font-normal leading-relaxed pb-10 ${styles.para}`}
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 400,
              }}
            >
              {t(sections[currentSection].detail)}
            </p>

            {/* Indicator */}
            <div className="absolute left-4 md:left-8 lg:left-8 xl:left-8 2xl:left-10 flex space-x-2">
              {sections.map((_, index) => (
                <div
                  key={index}
                  // onClick={() => handleSlideChange(index)}
                  className={`w-10 h-2 md:w-10 md:h-2 transition-all duration-300 ${
                    index === currentSection
                      ? 'bg-red-500 w-10 h-2 md:w-10 md:h-2'
                      : 'bg-white w-2 h-2 md:w-2 md:h-2'
                  }`}
                ></div>
              ))}
            </div>
          </div>
          {/* Arrow Section */}
          <div className="flex text-white items-center justify-end">
            {/* Left Arrow */}
            <button
              className=" hover:bg-opacity-20 transition pl-2 pb-2 pr-2"
              onClick={handlePrevious}
            >
              <img src={images.left_arrow} alt={'Left Arrow'} />
            </button>

            {/* Right Arrow */}
            <button
              className=" hover:bg-opacity-20 transition pl-2 pb-2 pr-2"
              onClick={handleNext}
            >
              <img src={images.right_arrow} alt={'Right Arrow'} />
            </button>
          </div>
        </div>
      </section>

      {/* ourTeam Section */}
      <section
        className={`relative flex flex-col items-center justify-center text-center font py-20 px-6 md:px-16 lg:px-20 2xl:px-16`}
      >
        <div
          className={`title text-center font-bold text-4xl 2xl:text-5xl ${styles.text}`}
        >
          <span>{t('ourTeam.ourTeamTitle')}</span>
        </div>
        <div
          className={`grid items-center grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 pt-14 ${styles.grid}`}
        >
          {ourTeam.map((teamMember, index) => (
            <div
              key={index}
              className="flex flex-col items-center justify-center"
            >
              <img
                src={teamMember.image}
                alt={t(teamMember.personName)}
                className={`w-52 h-auto mb-4 object-cover ${styles.team}`}
              />
              <h3
                className={`text-base 2xl:text-lg font-normal ${styles.name}`}
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t(teamMember.personName)}
              </h3>
              <p
                className={`text-sm 2xl:text-base font-normal text-gray-600 break-words ${styles.role}`}
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontWeight: 400,
                }}
              >
                {t(teamMember.personRole)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Map Section */}

      <Map />

      {/* Footer Section */}

      <Footer />
    </>
  );
};

export default About;
